import './layer-configuration-panel.css';

import h from 'hyperscript';
import arrowLeftIcon from '@disy/cadenza-icons/arrow-left.svg';
import debounce from 'lodash-es/debounce';
import { unByKey } from 'ol/Observable';

import 'ui/compact-header/compact-header.css';

import isEqualWith from 'lodash-es/isEqualWith';

import { Select } from 'ui/select/select';
import { Sidebar } from 'ui/sidebar/sidebar';

import { executeAfterAnimation } from 'cadenza/utils/animation-util';
import { context } from 'cadenza/map/context/context';
import { MapProperty } from 'cadenza/map/map-property';
import { LayerProperty } from 'cadenza/map/layer-property';
import { applyTemplate, getLayerTemplates, type LayerTemplateWebReferenceDto } from 'cadenza/api-client/layer-templates-api';
import { isConfigurableWithCurrentMapScale, refreshLayer } from 'cadenza/map/layer-utils';
import { logger } from 'cadenza/utils/logging';
import { LayerConfigApi } from 'cadenza/api-client/layer-config-api';
import { icon } from 'cadenza/utils/icon/icon';
import { ClassificationKind, type ClusterDto, type LayerConfigDto, VisualizationStrategy } from 'cadenza/api-client/layer-config-dto';
import type { Map } from 'cadenza/map/map';
import { ClusterConfiguration } from 'cadenza/map/layer-configuration-panel/cluster-configuration/cluster-configuration';
import { HeatmapConfiguration } from 'cadenza/map/layer-configuration-panel/heatmap-configuration/heatmap-configuration';
import { SymbolismConfiguration } from 'cadenza/map/layer-configuration-panel/symbolism-configuration/symbolism-configuration';
import { WebMapStyleConfiguration } from 'cadenza/map/layer-configuration-panel/webmapstyle-configuration/webmapstyle-configuration';

import type { EventsKey } from 'ol/events';

import i18n from './layer-configuration-panel.properties';

const DEBOUNCE_DELAY = 400;

const NAME = 'd-layer-configuration-panel';

export class LayerConfigurationPanel extends Sidebar {

  #debouncedSaveChanges = debounce(() => this._saveChanges(), DEBOUNCE_DELAY);
  #debouncedFetchConfig = debounce(() => this._getConfigFromBackend(), DEBOUNCE_DELAY);

  readonly #api: LayerConfigApi;
  readonly #map: Map;
  readonly #layerId: string;

  // initialized in connectedCallback
  _model!: LayerConfigDto;
  // initialized in connectedCallback
  _layerTemplates!: LayerTemplateWebReferenceDto[];
  _eventKeys: EventsKey[] = [];

  // child elements:
  _templateSelect?: Select;
  _visualizationStrategySelect?: HTMLSelectElement;
  _heatmapConfiguration?: HeatmapConfiguration;
  _symbolismConfiguration?: SymbolismConfiguration;
  _clusterConfiguration?: ClusterConfiguration;
  _webMapStyleConfiguration?: WebMapStyleConfiguration;

  constructor (layerId: string, map: Map) {
    super();
    this.#map = map;
    this.#layerId = layerId;
    this.#api = new LayerConfigApi(this.#layerId);
    this.addEventListener('change', this.#debouncedSaveChanges);
  }

  override connectedCallback () {
    super.connectedCallback();
    this.classList.add(NAME, 'is-animated', 'light-bg');
    this.open = false;

    Promise.all([
      this.#api.getConfig(context(this.#map).mapScale),
      this._getLayerTemplates()
    ]).then(([ config, templates ]) => {
      this._model = config;
      this._layerTemplates = templates;
      this._render();
      this._eventKeys = [
        // @ts-ignore
        this.#map.on(`change:${MapProperty.EDITING_LAYER}`, () => this.close()),
        this.#map.getView().on('change:resolution', this.#debouncedFetchConfig)
      ];
      this.open = true;
    }).catch(error => logger.error(error));
  }

  override disconnectedCallback () {
    super.disconnectedCallback();
    unByKey(this._eventKeys);
  }

  override close () {
    super.close();
    executeAfterAnimation(this, () => this.remove());
  }

  _render () {
    this.innerHTML = '';

    this._templateSelect = this._createTemplateSelect();
    this._updateTemplateSelectOptions();
    this._visualizationStrategySelect = this._createVisualizationStrategySelect();
    this._heatmapConfiguration = this._model.heatmap ? new HeatmapConfiguration(this._model.heatmap) : undefined;
    this._symbolismConfiguration = this._model.symbolism ? new SymbolismConfiguration(this._model.symbolism, this._model.label) : undefined;
    this._clusterConfiguration = createClusterConfigurationIfApplicable(this._model.classificationKind, this._model.cluster);
    // webMapStyle may be an empty string, so we explicitly check for undefined
    this._webMapStyleConfiguration = this._model.webMapStyle !== undefined ? new WebMapStyleConfiguration(this._model.webMapStyle, this.#map, this.#layerId) : undefined;

    const commonSettingsPanel = h('.d-layer-configuration-panel--common-settings-group', [
      this._templateSelect && h('form-group', [
        h('label', i18n('templateSelect.label')),
        this._templateSelect
      ]),
      this._visualizationStrategySelect && h('form-group.visualizationMode', [
        h('label', i18n('visualizationModeSelect.label')),
        this._visualizationStrategySelect
      ])
    ]);

    this.append(
      h('header.d-compact-header', [
        h('button.d-sidebar-back.button.button-icon.button-borderless',
          {
            type: 'button',
            title: i18n('common:back'),
            onclick: () => this.close()
          },
          icon(arrowLeftIcon)
        ),
        h('label.d-compact-header--title', i18n('title'))
      ]),
      h('.d-sidebar-content', [
        commonSettingsPanel.children.length > 0 ? commonSettingsPanel : null,
        this._heatmapConfiguration && h(
          'details.d-panel-section',
          {
            hidden: !includesHeatmap(this._model.visualizationStrategy),
            open: true
          },
          [
            h('summary', i18n('sections.heatmap.title')),
            this._heatmapConfiguration
          ]),
        this._symbolismConfiguration && h(
          'details.d-panel-section',
          {
            hidden: !includesSymbolism(this._model.visualizationStrategy),
            open: true
          },
          [
            h('summary', i18n('sections.symbolism.title')),
            this._symbolismConfiguration
          ]),
        this._clusterConfiguration && h(
          'details-d-panel-section',
          {
            open: true
          },
          [
            h('summary', ''),
            this._clusterConfiguration
          ]),
        this._webMapStyleConfiguration && h(
          'details-d-panel-section',
          {
            open: true
          },
          [
            h('summary', ''),
            this._webMapStyleConfiguration
          ])
      ])
    );
  }

  _saveChanges () {
    const config: LayerConfigDto = {
      visualizationStrategy: this._model.visualizationStrategy
    };

    if (this._visualizationStrategySelect) {
      config.visualizationStrategy = this._visualizationStrategySelect.value as VisualizationStrategy;
    }

    if (this._heatmapConfiguration) {
      config.heatmap = this._heatmapConfiguration.state;
    }

    if (this._symbolismConfiguration) {
      config.symbolism = this._symbolismConfiguration.state;
      config.label = this._symbolismConfiguration.labelState;
    }

    config.cluster = Object.assign({}, this._model.cluster) as ClusterDto; // clone
    if (this._clusterConfiguration) {
      config.cluster.showClassificationInCluster = this._clusterConfiguration.state.showClassificationInCluster;
    }

    if (this._webMapStyleConfiguration) {
      config.webMapStyle = this._webMapStyleConfiguration.state.selectedStyle;
    }

    this._model = config;

    this.#api.setConfig(config, context(this.#map).mapScale)
      .then(() => {
        refreshLayer(this._layer);
        return this._getLayerTemplates();
      })
      .then(templates => {
        this._layerTemplates = templates;
        this._updateTemplateSelectOptions();
        this._updateSectionsVisibility();
      })
      .catch(error => logger.error(error));
  }

  _getLayerTemplates (): Promise<LayerTemplateWebReferenceDto[]> {
    if (this._layer.get(LayerProperty.HAS_APPLICABLE_TEMPLATES)) {
      return getLayerTemplates(this.#layerId, context(this.#map).mapScale);
    }
    return Promise.resolve([]);
  }

  _createTemplateSelect (): Select | undefined {
    if (this._layerTemplates.length === 0) {
      return undefined;
    }

    const data = this._layerTemplates.map(({ id, active, printName }) => ({
      id,
      text: printName,
      selected: active
    }));

    const select = new Select({
      placeholder: `( ${i18n('templateSelect.custom')} )`,
      data
    });
    select.addEventListener('change', event => {
      event.stopPropagation();
      this._applyLayerTemplate(select);
    });
    return select;
  }

  _updateTemplateSelectOptions () {
    const id = this._layerTemplates.find(template => template.active)?.id;
    if (this._templateSelect && id) {
      this._templateSelect.selection = id;
    }
  }

  _createVisualizationStrategySelect (): HTMLSelectElement | undefined {
    if (!this._model.heatmap) {
      return undefined;
    }

    const enabledStrategies = [];

    if (this._model.heatmap) {
      enabledStrategies.push(VisualizationStrategy.HEATMAP);
    }
    if (this._model.symbolism) {
      enabledStrategies.push(VisualizationStrategy.SYMBOLISM);
    }
    if (this._model.symbolism && this._model.heatmap) {
      enabledStrategies.push(VisualizationStrategy.SYMBOLISM_AND_HEATMAP);
    }

    return h('select',
      { disabled: enabledStrategies.length <= 1 },
      createOptionElements(
        enabledStrategies.map(value => ({
          value,
          label: i18n(`visualizationModeSelect.values.${value}`)
        })),
        this._model.visualizationStrategy)) as HTMLSelectElement;
  }

  _applyLayerTemplate (templateSelect: Select) {
    if (!templateSelect.selection.length) {
      return;
    }
    const selectedTemplateId = templateSelect.selection[0].id;
    let selectedTemplate: LayerTemplateWebReferenceDto;
    this._layerTemplates.forEach(template => {
      if (template.id === selectedTemplateId) {
        selectedTemplate = template;
      }
      template.active = (template.id === selectedTemplateId);
    });
    // selectedTemplate can never be undefined, because _layerTemplates cannot be empty, and one of the ids always matches
    applyTemplate(this.#layerId, selectedTemplate!)
      .then(() => context(this.#map).pull())
      .then(() => {
        refreshLayer(this._layer);
        return this._getConfigFromBackend();
      })
      .catch(error => logger.error(error));
  }

  _getConfigFromBackend () {
    if (!isConfigurableWithCurrentMapScale(this._layer, this.#map)) {
      this.close();
      return;
    }
    return this.#api.getConfig(context(this.#map).mapScale)
      .then(result => {
        if (isEqualWith(this._model, result, (vm, vr, prop) => prop === 'availableGradients' || undefined)) {
          return;
        }
        this._model = result;
        this._render();
      });
  }

  _updateSectionsVisibility () {
    if (this._heatmapConfiguration) {
      (this._heatmapConfiguration.closest('.d-panel-section') as HTMLElement).hidden = !includesHeatmap(this._model.visualizationStrategy);
    }
    if (this._symbolismConfiguration) {
      (this._symbolismConfiguration.closest('.d-panel-section') as HTMLElement).hidden = !includesSymbolism(this._model.visualizationStrategy);
    }
  }

  get _layer () {
    return context(this.#map).getLayer(this.#layerId)!;
  }

}

customElements.define(NAME, LayerConfigurationPanel);

function createOptionElements (options: { value: string; label: string }[], selectedValue: string | undefined) {
  return options.map(({ value, label }) => {
    const attributes = { value, selected: value === selectedValue };
    return h('option', attributes, label);
  });
}

function createClusterConfigurationIfApplicable (classificationKind: ClassificationKind | undefined, cluster: ClusterDto | undefined) {
  if (!classificationKind || classificationKind === ClassificationKind.NONE || !cluster || !cluster.enabled) {
    return undefined;
  }
  return new ClusterConfiguration(cluster);
}

function includesHeatmap (visualizationStrategy: string | undefined) {
  return visualizationStrategy === VisualizationStrategy.HEATMAP || visualizationStrategy === VisualizationStrategy.SYMBOLISM_AND_HEATMAP;
}

function includesSymbolism (visualizationStrategy: string | undefined) {
  return visualizationStrategy === VisualizationStrategy.SYMBOLISM || visualizationStrategy === VisualizationStrategy.SYMBOLISM_AND_HEATMAP;
}
